"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBrands,
  createBrand,
  updateBrand,
  deleteBrand,
} from "@/store/modules/brands/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { brandSchema } from "@/validations";
import { usePermission } from "../usePermission/usePermission";
import { useApiReady } from "@/hooks/useApiReady";

const useBrand = (onSuccess?: () => void) => {
  const { params, setParams, handlePage } = useParams();
  const { isReady: isApiReady } = useApiReady();
  const dispatch: AppDispatch = useDispatch();
  const [brandSlug, setBrandSlug] = useState<string | null>(null);
  const { hasPermission, loading: loadingPermission } = usePermission();

  useEffect(() => {
    if (!isApiReady) return;

    if (hasPermission("read brand")) {
      dispatch(fetchBrands(params));
    }
  }, [params, isApiReady, dispatch]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(brandSchema),
    defaultValues: {
      name: "",
    },
  });

  const setFormData = (data: Partial<typeof brandSchema.__outputType>) => {
    reset(data);
  };

  const onSubmit = async (data: typeof brandSchema.__outputType) => {
    try {
      let result;

      if (brandSlug && hasPermission("update brand")) {
        result = await dispatch(updateBrand({ slug: brandSlug, ...data }));
      } else {
        if (hasPermission("create brand")) {
          result = await dispatch(createBrand(data));
        }
      }

      if (
        createBrand.fulfilled.match(result) ||
        updateBrand.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        reset();
        setBrandSlug(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados da marca:", error);
    }
  };

  const { brands, loading, error } = useSelector((state: any) => state.brands);

  const totalItems = brands?.total ?? 0;
  const itemsPerPage = brands?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const currentPage = params.current_page ?? 1;

  const handlePageChange = (page: number) => {
    handlePage(page);
  };

  const handleDelete = async () => {
    try {
      if (brandSlug && hasPermission("delete brand")) {
        const result = await dispatch(deleteBrand(brandSlug));
        setBrandSlug(null);
        return true;
        // if (deleteBrand.fulfilled.match(result)) {
        //     if (onSuccess) onSuccess();
        //     setBrandSlug(null);
        // }
      }
    } catch (error) {
      console.error("Erro ao excluir a marca:", error);
    }
  };

  return {
    register,
    handleSubmit,
    reset,
    errors,
    isSubmitting,
    setFormData,
    onSubmit,
    brands,
    loading,
    error,
    setBrandSlug,
    brandSlug,
    handleDelete,
    params,
    setParams,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
  };
};

export default useBrand;
